import { useState, useRef, useEffect } from 'react';
import { chatWithAI, getSearchSuggestions } from '../../services/AIService';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';

function AIAgent() {
    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: 'Olá! Sou o EasyCripto AI, seu assistente especialista em criptomoedas e trading. Como posso ajudá-lo hoje?',
            timestamp: new Date().toISOString()
        }
    ]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [notification, setNotification] = useState({ type: '', text: '' });
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) && 
                inputRef.current && !inputRef.current.contains(event.target)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    const debouncedSearch = useRef(
        debounce(async (query) => {
            if (query.length > 1) {
                try {
                    const data = await getSearchSuggestions(query);
                    setSuggestions(data || []);
                    setShowSuggestions(true);
                } catch (err) {
                    console.error('Erro ao buscar sugestões:', err);
                    setSuggestions([]);
                }
            } else {
                setSuggestions([]);
                setShowSuggestions(false);
            }
        }, 300)
    ).current;

    function handleInputChange(e) {
        const value = e.target.value;
        setInputMessage(value);
        debouncedSearch(value);
    }

    function handleInputFocus() {
        if (suggestions.length > 0) {
            setShowSuggestions(true);
        }
    }

    function handleSuggestionClick(coin) {
        setInputMessage(coin.name);
        setShowSuggestions(false);
        // Não busca automaticamente, apenas preenche o campo
    }

    async function handleSendMessage(event) {
        event.preventDefault();
        
        if (!inputMessage.trim() || isLoading) return;

        setShowSuggestions(false);
        const userMessage = inputMessage.trim();
        setInputMessage('');
        
        // Adicionar mensagem do usuário
        const newUserMessage = {
            role: 'user',
            content: userMessage,
            timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, newUserMessage]);
        setIsLoading(true);

        try {
            // Preparar histórico (apenas as últimas 10 mensagens)
            const history = messages
                .slice(-10)
                .map(msg => ({
                    role: msg.role,
                    content: msg.content
                }));

            const response = await chatWithAI(userMessage, history);

            // Adicionar resposta da IA
            const aiMessage = {
                role: 'assistant',
                content: response.response || response.reply || 'Desculpe, não consegui processar sua mensagem.',
                timestamp: response.timestamp || new Date().toISOString()
            };

            setMessages(prev => [...prev, aiMessage]);
        } catch (error) {
            console.error('Erro ao enviar mensagem:', error);
            const errorMessage = error.response?.data || error.message || 'Erro desconhecido ao comunicar com a IA';
            
            setNotification({ 
                type: 'error', 
                text: errorMessage 
            });

            // Adicionar mensagem de erro
            const errorMsg = {
                role: 'assistant',
                content: `Desculpe, ocorreu um erro: ${errorMessage}`,
                timestamp: new Date().toISOString()
            };
            setMessages(prev => [...prev, errorMsg]);
        } finally {
            setIsLoading(false);
            inputRef.current?.focus();
        }
    }

    function formatTime(timestamp) {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }

    return (
        <>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h1 className="h4">Agente IA</h1>
                        <p className="text-muted">Assistente especialista em criptomoedas e trading</p>
                    </div>
                </div>

                <div className="card card-body border-0 shadow">
                    {/* Área de mensagens */}
                    <div 
                        className="border rounded p-3 mb-3" 
                        style={{ 
                            height: '500px', 
                            overflowY: 'auto',
                            backgroundColor: '#f8f9fa'
                        }}
                    >
                        {messages.map((message, index) => (
                            <div 
                                key={index} 
                                className={`mb-3 d-flex ${message.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}
                            >
                                <div 
                                    className={`p-3 rounded ${message.role === 'user' 
                                        ? 'bg-primary text-white' 
                                        : 'bg-white border'
                                    }`}
                                    style={{ 
                                        maxWidth: '70%',
                                        wordWrap: 'break-word'
                                    }}
                                >
                                    <div className="d-flex justify-content-between align-items-start mb-1">
                                        <strong className="small">
                                            {message.role === 'user' ? 'Você' : 'EasyCripto AI'}
                                        </strong>
                                        <small className={`ms-2 ${message.role === 'user' ? 'text-white-50' : 'text-muted'}`}>
                                            {formatTime(message.timestamp)}
                                        </small>
                                    </div>
                                    <div style={{ whiteSpace: 'pre-wrap' }}>
                                        {message.content}
                                    </div>
                                </div>
                            </div>
                        ))}
                        {isLoading && (
                            <div className="d-flex justify-content-start mb-3">
                                <div className="bg-white border p-3 rounded">
                                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                                        <span className="visually-hidden">Carregando...</span>
                                    </div>
                                    <span className="text-muted">EasyCripto AI está pensando...</span>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Campo de input */}
                    <form onSubmit={handleSendMessage}>
                        <div className="input-group position-relative" ref={suggestionsRef}>
                            <input
                                ref={inputRef}
                                type="text"
                                className="form-control"
                                placeholder="Digite sua mensagem..."
                                value={inputMessage}
                                onChange={handleInputChange}
                                onFocus={handleInputFocus}
                                disabled={isLoading}
                                maxLength={2000}
                            />
                            <button 
                                className="btn btn-primary" 
                                type="submit"
                                disabled={isLoading || !inputMessage.trim()}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Enviando...
                                    </>
                                ) : (
                                    <>
                                        <svg className="icon icon-xs me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                        </svg>
                                        Enviar
                                    </>
                                )}
                            </button>
                            {showSuggestions && suggestions.length > 0 && (
                                <div 
                                    className="position-absolute w-100 bg-white border rounded shadow-lg"
                                    style={{
                                        top: '100%',
                                        left: 0,
                                        zIndex: 1000,
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        marginTop: '4px'
                                    }}
                                >
                                    {suggestions.map((coin, index) => (
                                        <div
                                            key={index}
                                            className="p-2 border-bottom cursor-pointer"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => handleSuggestionClick(coin)}
                                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="fw-bold">{coin.name}</span>
                                                <span className="badge bg-secondary">{coin.symbol.toUpperCase()}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <small className="text-muted d-block mt-2">
                            {inputMessage.length}/2000 caracteres
                        </small>
                    </form>
                </div>

                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
        </>
    );
}

export default AIAgent;

